import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Activity, ChartBar, ChartCandlestick, CreditCard, FileText, HelpCircle, LayoutDashboard,
  Layers, LineChart, ListChecks, LogOut, Menu, Moon, PanelLeft, PanelLeftClose, Newspaper, PieChart, Rss, Settings, ShoppingCart, Star, Sun, TrendingUp, UserCircle, X, BarChart3 } from 'lucide-react';
import { Button } from '../components/ui';
import { Toaster, toast } from '../components/Toaster';
import { logout, refreshUser, useUser } from '../hooks/useAuth';
import { useTheme } from '../hooks/useTheme';
import { useEchoSubscription } from '../hooks/useEcho';
import { useAuthStore } from '../stores/authStore';
import { cn, formatINR } from '../lib/utils';

const navItems = [
  { to: '/dashboard',  label: 'Dashboard',  icon: LayoutDashboard },
  { to: '/portfolio',  label: 'Portfolio',  icon: PieChart },
  { to: '/positions',  label: 'Positions',  icon: TrendingUp },
  // /trade (singular) — manual buy/sell page. The plural /trades is the
  // history list. Order chosen so a user sees Trade next to Positions,
  // matching the "look at portfolio, then place a trade" flow.
  { to: '/trade',      label: 'Trade',      icon: ShoppingCart },
  { to: '/trades',     label: 'Trades',     icon: ListChecks },
  { to: '/charts',     label: 'Charts',     icon: ChartCandlestick },
  { to: '/option-chain', label: 'Option Chain', icon: Layers },
  { to: '/open-interest', label: 'Open Interest', icon: BarChart3 },
  { to: '/prediction-review', label: 'Prediction Review', icon: LineChart },
  { to: '/strategies', label: 'Strategies', icon: ChartBar },
  { to: '/signals',    label: 'Signals',    icon: Activity },
  { to: '/news-signal', label: 'News Signal', icon: Rss },
  { to: '/watchlists', label: 'Watchlists', icon: Star },
  { to: '/news',       label: 'News',       icon: Newspaper },
  { to: '/billing',    label: 'Billing',    icon: CreditCard },
  { to: '/profile',    label: 'Profile',    icon: UserCircle },
  { to: '/settings',   label: 'Settings',   icon: Settings },
  { to: '/help',       label: 'How it works', icon: HelpCircle },
];

export function AppLayout() {
  const user = useUser();
  const loading = useAuthStore((s) => s.loading);
  const nav = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Desktop sidebar collapse (icon-only rail), remembered across sessions.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem('nav-collapsed') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('nav-collapsed', collapsed ? '1' : '0'); } catch { /* ignore */ }
  }, [collapsed]);

  const { theme, toggle } = useTheme();

  // Auto-close the mobile drawer on route change.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Lock body scroll while the drawer is open so the page underneath
  // doesn't move when the user scrolls the nav.
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
      // Always CLEAR on cleanup — never restore a captured previous
      // value. A second lock capturing this 'hidden' and restoring it
      // froze scrolling permanently (2026-07-29).
      return () => { document.body.style.overflow = ''; };
    }
  }, [mobileOpen]);

  // Subscribe to user channel; pop a toast on each trade.executed event,
  // and a prominent warning when auto-trading is paused (daily-loss cap hit).
  useEchoSubscription(
    (event) => {
      toast({
        title: `Trade executed: ${event.side.toUpperCase()} ${event.quantity}`,
        description: `${formatINR(event.fill_price)} — ${event.explanation}`,
        variant: 'success',
      });
    },
    (event) => {
      toast({
        title: 'Auto-trading paused — daily loss limit reached',
        description: event.message,
        variant: 'destructive',
      });
      // Pull the fresh user so the Settings auto-trade toggle shows "off" now.
      void refreshUser();
    },
  );

  useEffect(() => {
    if (!loading && !user) nav('/login', { replace: true });
  }, [loading, user, nav]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;

  if (!user) return null;

  return (
    <div className="min-h-screen flex flex-col bg-muted/20">
      <header className="clay-bar sticky top-0 z-30 h-14 px-4 md:px-6 flex items-center justify-between gap-3">
        {/* Brand zone: on desktop it spans exactly the sidebar width so the
            rail toggle lands ON the sidebar/content boundary (operator
            screenshot 2026-07-28). Width stays fixed when the rail
            collapses so the button never jumps under the cursor. */}
        <div className="flex items-center gap-2 lg:w-56 lg:shrink-0 lg:pr-3">
          {/* Hamburger — visible only below lg */}
          <button
            type="button"
            aria-label="Open menu"
            onClick={() => setMobileOpen(true)}
            className="clay-btn lg:hidden inline-flex h-9 w-9 items-center justify-center hover:bg-accent"
          >
            <Menu className="h-5 w-5" />
          </button>
          {/* Full lockup on every breakpoint — the wordmark used to be
              hidden below sm, leaving phones with a bare bar-chart glyph
              and no way to tell whose app this is. min-w-0 lets it shrink
              rather than push the theme/logout controls off-screen. */}
          <Link to="/dashboard" className="flex min-w-0 items-center gap-1.5" aria-label="Nithi Trade">
            <img src="/logo-icon.svg" alt="" className="h-7 w-7 shrink-0" />
            <img src="/logo-wordmark.svg" alt="Nithi Trade" className="h-6 min-w-0 sm:h-7 dark:brightness-0 dark:invert" />
          </Link>
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
            aria-expanded={!collapsed}
            title={collapsed ? 'Expand menu' : 'Collapse menu'}
            className="clay-btn ml-auto hidden lg:inline-flex h-9 w-9 items-center justify-center text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            {collapsed ? <PanelLeft className="h-[18px] w-[18px]" /> : <PanelLeftClose className="h-[18px] w-[18px]" />}
          </button>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 text-sm">
          <Link to="/profile" className="hidden max-w-[200px] truncate text-muted-foreground transition-colors hover:text-foreground sm:inline" title="Your profile">{user.email}</Link>
          <button
            type="button"
            onClick={toggle}
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
            className="clay-btn inline-flex h-9 w-9 items-center justify-center text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            {theme === 'dark' ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
          </button>
          <Button variant="ghost" size="sm" onClick={async () => { await logout(); nav('/login'); }}>
            <LogOut className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">Logout</span>
          </Button>
        </div>
      </header>

      {/* Mobile drawer + backdrop */}
      {mobileOpen && (
        <>
          <div
            aria-hidden
            className="lg:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          {/* Mobile drawer. 17 nav items on a 700px-tall phone means the list
              MUST scroll — so the brand row and the account footer are pinned
              and only the list moves. Narrower than before (240px vs 256px) so
              more of the page stays visible behind it, and the right edge is
              rounded to match the clay language. */}
          <aside className="clay-rail lg:hidden fixed inset-y-0 left-0 z-50 flex w-[15rem] flex-col rounded-r-[1.75rem] p-2.5">
            <div className="flex flex-shrink-0 items-center justify-between px-1.5 pb-2">
              <img src="/logo-wordmark.svg" alt="Nithi Trade" className="h-6 dark:brightness-0 dark:invert" />
              <button
                type="button"
                aria-label="Close menu"
                onClick={() => setMobileOpen(false)}
                className="clay-btn -mr-1 inline-flex h-8 w-8 items-center justify-center hover:bg-accent"
              >
                <X className="h-[18px] w-[18px]" />
              </button>
            </div>
            {/* overscroll-contain stops the page behind scrolling once the
                list hits its end (iOS rubber-band leak). */}
            <nav className="-mx-0.5 flex-1 space-y-0.5 overflow-y-auto overscroll-contain px-0.5 py-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) => cn(
                    'clay-nav flex items-center gap-2.5 px-3 py-[0.55rem] text-[13px] font-medium',
                    isActive
                      ? 'clay-nav-active bg-brand/10 font-semibold text-foreground dark:bg-brand/20'
                      : 'text-foreground/75 hover:bg-brand/5 hover:text-foreground',
                  )}
                >
                  {({ isActive }) => (
                    <>
                      <item.icon className={cn('h-[18px] w-[18px] flex-shrink-0',
                        isActive ? 'text-brand' : 'text-foreground/45')} />
                      <span className="truncate">{item.label}</span>
                    </>
                  )}
                </NavLink>
              ))}
            </nav>
            <div className="mt-1.5 flex-shrink-0 truncate border-t px-1.5 pt-2.5 text-[11px] text-muted-foreground">{user.email}</div>
          </aside>
        </>
      )}

      <div className="flex-1 flex">
        <aside
          className={cn(
            'clay-rail hidden lg:flex flex-col p-3 transition-[width] duration-200',
            // Pinned under the sticky header with its own scroll, so the
            // nav never slides away with the page content.
            'sticky top-14 h-[calc(100vh-3.5rem)] overflow-y-auto overscroll-contain',
            collapsed ? 'w-16' : 'w-56',
          )}
        >
          <nav className="flex-1 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                title={collapsed ? item.label : undefined}
                className={({ isActive }) => cn(
                  'clay-nav flex items-center gap-2.5 py-2 text-sm font-medium',
                  collapsed ? 'justify-center px-0' : 'px-3',
                  isActive
                    ? 'clay-nav-active bg-brand/10 font-semibold text-foreground dark:bg-brand/20'
                    : 'text-foreground/75 hover:bg-brand/5 hover:text-foreground',
                )}
              >
                {({ isActive }) => (
                  <>
                    <item.icon className={cn('h-[18px] w-[18px] shrink-0',
                      isActive ? 'text-brand' : 'text-foreground/45')} />
                    {!collapsed && <span className="truncate">{item.label}</span>}
                  </>
                )}
              </NavLink>
            ))}
          </nav>
        </aside>
        <main className="flex-1 p-4 sm:p-6 min-w-0">{/* No max-width: capping at screen-xl left a dead gutter on the right when the rail collapsed, so charts never grew into the space that was freed (operator, 2026-07-29). */}
          {user.kyc_status !== 'approved' && (
            <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
              <div>
                <FileText className="inline h-4 w-4 mr-1" />
                {user.kyc_status === 'pending' && <>Complete KYC to enable trading.</>}
                {user.kyc_status === 'in_review' && <>Your KYC is under review. We'll email you once approved.</>}
                {user.kyc_status === 'rejected' && <>Your KYC was rejected. Contact support.</>}
              </div>
              {user.kyc_status === 'pending' && (
                <Link to="/kyc"><Button size="sm">Submit KYC</Button></Link>
              )}
            </div>
          )}
          <Outlet />
        </main>
      </div>
      <Toaster />
    </div>
  );
}
